import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { exportFilename } from '../../utils/nomenclature';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LabelList, Cell } from 'recharts';

// Export libraries are heavy — load them lazily only when the user exports.
let _ExcelJS = null;
async function loadExcelLib() {
  if (!_ExcelJS) { _ExcelJS = (await import('exceljs')).default; }
  return _ExcelJS;
}
let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * MTO multi-revision comparison (Plan C).
 * Compares the bid -> construct evolution across revisions: per-revision totals
 * (qty / labour hours / material cost / labour cost / grand total), a per-ISO
 * pivot/diff table, and an AI-written insight paragraph.
 */
const METRICS = [
  { key: 'qty', label: 'Quantity', fmt: (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) },
  { key: 'hours', label: 'Labour Hours', fmt: (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) },
  { key: 'material_cad', label: 'Material Cost (CAD)', money: true },
  { key: 'labour_cad', label: 'Labour Cost (CAD)', money: true },
  { key: 'grand_cad', label: 'Grand Total (CAD)', money: true },
];
// Whitelisted pivot dimensions (must match the backend /compare PIVOT_DIMENSIONS).
const PIVOT_DIMS = [
  { key: 'material', label: 'Material' },
  { key: 'matbore', label: 'MatBore' },
  { key: 'type', label: 'Type' },
  { key: 'sch', label: 'Sch' },
  { key: 'bore', label: 'Bore' },
  { key: 'rating', label: 'Rating' },
  { key: 'end_type', label: 'End Type' },
  { key: 'seam_type', label: 'Seam Type' },
  { key: 'piping_class', label: 'Piping Class' },
  { key: 'fluid_code', label: 'Fluid Code' },
  { key: 'fabrication_shop', label: 'Fab Shop' },
  { key: 'priority', label: 'Priority' },
  { key: 'spool_number', label: 'Spool #' },
  { key: 'iso_sheet_number', label: 'ISO Sheet #' },
  { key: 'iso_version', label: 'ISO Version' },
  { key: 'system', label: 'System' },
  { key: 'subsystem', label: 'Subsystem' },
];
const money = (v) => '$' + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const REV_PALETTE = ['#818cf8', '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#312e81', '#a5b4fc', '#c7d2fe'];

// KPI card: metric value at the latest selected revision + delta vs the first.
const KpiCard = ({ label, first, last, fmt, icon }) => {
  const delta = last - first;
  const pct = first !== 0 ? (delta / Math.abs(first)) * 100 : null;
  const up = delta > 0.005, down = delta < -0.005;
  const color = up ? '#16a34a' : (down ? '#dc2626' : '#64748b');
  return (
    <div style={{ flex: '1 1 150px', minWidth: 150, background: 'linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.7rem 0.9rem', boxShadow: '0 1px 2px rgba(15,23,42,0.05)' }}>
      <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>{icon} {label}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>{fmt(last)}</div>
      <div style={{ fontSize: '0.72rem', color, fontWeight: 700 }}>
        {up ? '▲' : (down ? '▼' : '—')} {fmt(Math.abs(delta))}{pct !== null && (up || down) ? ` (${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)` : ''}
        <span style={{ color: '#94a3b8', fontWeight: 400 }}> vs first rev</span>
      </div>
    </div>
  );
};

// Colored status pill for the ISO diff table.
const StatusPill = ({ status }) => {
  const cfg = {
    added: { bg: '#dcfce7', fg: '#15803d', icon: '＋' },
    removed: { bg: '#fee2e2', fg: '#b91c1c', icon: '－' },
    changed: { bg: '#fef3c7', fg: '#b45309', icon: '~' },
  }[status] || { bg: '#f1f5f9', fg: '#64748b', icon: '' };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, background: cfg.bg, color: cfg.fg, fontSize: '0.7rem', fontWeight: 700, textTransform: 'capitalize' }}>
      {cfg.icon} {status}
    </span>
  );
};
// Display name for a comparison column: client revs show "Rev N"; the synthetic
// ISO roll-up (rev = -1) shows "Consolidated ISOs".
const revName = (r) => (r && r.is_consolidated ? 'Consolidated ISOs' : `Rev ${r ? r.rev : ''}`);

export default function MtoCompareModal({ isOpen, onClose, estimateNumber, revisions = [] }) {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metric, setMetric] = useState('hours');
  const [baseRev, setBaseRev] = useState(null);
  const [targetRev, setTargetRev] = useState(null);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  // Plan 12: multi-rev selection + configurable pivot.
  const [selectedRevs, setSelectedRevs] = useState(null); // null = all
  const [pivotDim, setPivotDim] = useState('');
  const [pivotMeasure, setPivotMeasure] = useState('hours');
  const [pivotData, setPivotData] = useState(null);
  const [pivotLoading, setPivotLoading] = useState(false);
  // ISO churn (how the ISO-batch stream evolved across batches).
  const [churn, setChurn] = useState(null);
  const [churnLoading, setChurnLoading] = useState(false);

  const fetchCompare = useCallback(async () => {
    if (!estimateNumber) return;
    setLoading(true); setError(null);
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/compare?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data?.success) {
        setData(resp.data);
        const revs = (resp.data.revs || []).map((r) => r.rev);
        setSelectedRevs(revs);
        if (revs.length >= 2) { setBaseRev(revs[0]); setTargetRev(revs[revs.length - 1]); }
        else if (revs.length === 1) { setBaseRev(revs[0]); setTargetRev(revs[0]); }
      } else setError(resp.data?.message || 'Failed to load comparison');
    } catch (err) { setError(err?.response?.data?.message || 'Failed to load comparison'); }
    finally { setLoading(false); }
  }, [instance, accounts, estimateNumber]);

  const fetchChurn = useCallback(async () => {
    if (!estimateNumber) return;
    setChurnLoading(true);
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/iso-churn?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data?.success) setChurn(resp.data); else setChurn(null);
    } catch (err) { setChurn(null); }
    finally { setChurnLoading(false); }
  }, [instance, accounts, estimateNumber]);

  useEffect(() => { if (isOpen) { setInsight(null); setPivotData(null); setPivotDim(''); setChurn(null); fetchCompare(); fetchChurn(); } }, [isOpen, fetchCompare, fetchChurn]);

  // Fetch pivot (dimension × revisions) when a dimension is chosen.
  const fetchPivot = useCallback(async (dim, revs) => {
    if (!estimateNumber || !dim) { setPivotData(null); return; }
    setPivotLoading(true);
    try {
      const revsParam = revs && revs.length ? `&revs=${revs.join(',')}` : '';
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/compare?estimate_number=${encodeURIComponent(estimateNumber)}&pivot=${encodeURIComponent(dim)}${revsParam}`);
      if (resp.data?.success) setPivotData({ byDim: resp.data.byDim || [], pivotDim: resp.data.pivotDim });
      else setPivotData({ byDim: [], pivotDim: dim });
    } catch (err) { setPivotData({ byDim: [], pivotDim: dim }); }
    finally { setPivotLoading(false); }
  }, [instance, accounts, estimateNumber]);

  useEffect(() => {
    if (isOpen && pivotDim) fetchPivot(pivotDim, selectedRevs);
  }, [isOpen, pivotDim, selectedRevs, fetchPivot]);

  const allRevs = data?.revs || [];
  // The revisions actually shown (respecting the multi-select).
  const revList = useMemo(() => {
    if (!selectedRevs) return allRevs;
    const set = new Set(selectedRevs);
    return allRevs.filter((r) => set.has(r.rev));
  }, [allRevs, selectedRevs]);
  const toggleRev = (rev) => {
    setSelectedRevs((prev) => {
      const base = prev || allRevs.map((r) => r.rev);
      const set = new Set(base);
      if (set.has(rev)) { if (set.size > 1) set.delete(rev); } else set.add(rev);
      return allRevs.map((r) => r.rev).filter((x) => set.has(x));
    });
  };
  const chartData = useMemo(() => {
    if (!data) return [];
    return revList.map((r) => ({
      name: r.is_consolidated ? 'Consolidated ISOs' : `Rev ${r.rev}${r.client_rev ? ` (${r.client_rev})` : ''}`,
      value: data.totals?.[r.rev]?.[metric] || 0,
    }));
  }, [data, revList, metric]);

  // Per-ISO diff between base and target rev.
  const isoDiff = useMemo(() => {
    if (!data || baseRev === null || targetRev === null) return [];
    const rows = (data.byIso || []).map((it) => {
      const b = it.byRev?.[baseRev] || {};
      const t = it.byRev?.[targetRev] || {};
      const dHours = (t.hours || 0) - (b.hours || 0);
      const dQty = (t.qty || 0) - (b.qty || 0);
      const dCad = (t.material_cad || 0) + (t.labour_cad || 0) - ((b.material_cad || 0) + (b.labour_cad || 0));
      return {
        iso: it.iso_sheet_number,
        baseQty: b.qty || 0, targetQty: t.qty || 0, dQty,
        baseHours: b.hours || 0, targetHours: t.hours || 0, dHours,
        dCad,
        verB: b.iso_version, verT: t.iso_version,
        status: !b.qty && t.qty ? 'added' : (b.qty && !t.qty ? 'removed' : (Math.abs(dHours) > 0.01 || (b.iso_version || '') !== (t.iso_version || '') ? 'changed' : 'same')),
      };
    });
    return rows.filter((r) => r.status !== 'same').sort((a, b) => Math.abs(b.dHours) - Math.abs(a.dHours));
  }, [data, baseRev, targetRev]);

  const fetchInsight = async () => {
    setInsightLoading(true); setInsight(null);
    try {
      // 3+ selected revisions -> multi-rev series narrative; otherwise base→target.
      const useSeries = (selectedRevs || []).length >= 3;
      const body = useSeries
        ? { estimate_number: estimateNumber, revs: selectedRevs }
        : { estimate_number: estimateNumber, base_rev: baseRev, target_rev: targetRev };
      if (!useSeries && (baseRev === null || targetRev === null)) { setInsightLoading(false); return; }
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/compare/insight', body);
      if (resp.data?.success) setInsight(resp.data);
      else setInsight({ insight: resp.data?.message || 'Failed to generate insight', usedAi: false });
    } catch (err) { setInsight({ insight: err?.response?.data?.message || 'Failed to generate insight', usedAi: false }); }
    finally { setInsightLoading(false); }
  };

  // Pivot rows: dimension values with a measure per selected revision + total.
  const pivotRows = useMemo(() => {
    if (!pivotData?.byDim) return [];
    const revs = revList.map((r) => r.rev);
    const firstRev = revs[0];
    const lastRev = revs[revs.length - 1];
    return pivotData.byDim
      .map((d) => {
        const byRev = {}; let total = 0;
        revs.forEach((rev) => { const v = d.byRev?.[rev]?.[pivotMeasure] || 0; byRev[rev] = v; total += v; });
        // Δ = change from the first to the last selected revision (bid → latest).
        const delta = revs.length >= 2 ? ((byRev[lastRev] || 0) - (byRev[firstRev] || 0)) : 0;
        return { key: d.key, byRev, total, delta };
      })
      .filter((r) => r.total !== 0)
      .sort((a, b) => b.total - a.total);
  }, [pivotData, revList, pivotMeasure]);

  // ----- Export (xlsx / pdf / email), mirroring the Piping analytics dashboard -----
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const dateStamp = () => new Date().toISOString().slice(0, 10);
  const measureLabel = (key) => (METRICS.find((m) => m.key === key) || {}).label || key;

  const buildWorkbook = useCallback(async () => {
    const ExcelJS = await loadExcelLib();
    const wb = new ExcelJS.Workbook();
    wb.creator = 'LGL Tools';
    wb.created = new Date();
    const styleHeader = (row) => { row.font = { bold: true }; row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } }; }); };

    // Per-revision totals
    const s1 = wb.addWorksheet('Per-revision totals');
    styleHeader(s1.addRow(['Metric', ...revList.map((r) => revName(r))]));
    METRICS.forEach((m) => s1.addRow([m.label, ...revList.map((r) => Number(data.totals?.[r.rev]?.[m.key] || 0))]));
    s1.addRow(['ISO sheets', ...revList.map((r) => data.totals?.[r.rev]?.isoCount || 0)]);
    s1.columns.forEach((c) => { c.width = 22; });

    // ISO diff (base → target)
    const s2 = wb.addWorksheet('ISO diff');
    styleHeader(s2.addRow(['ISO Sheet', 'Status', 'Version (base→target)', 'Base Qty', 'Target Qty', 'Δ Qty', 'Base Hours', 'Target Hours', 'Δ Hours', 'Δ Cost (CAD)']));
    isoDiff.forEach((r) => s2.addRow([r.iso, r.status, `${r.verB ?? '-'} → ${r.verT ?? '-'}`, r.baseQty, r.targetQty, r.dQty, r.baseHours, r.targetHours, r.dHours, r.dCad]));
    s2.columns.forEach((c) => { c.width = 16; });

    // Pivot (only if a dimension is chosen)
    if (pivotDim && pivotRows.length) {
      const dimLabel = (PIVOT_DIMS.find((d) => d.key === pivotDim) || {}).label || pivotDim;
      const s3 = wb.addWorksheet('Pivot');
      s3.addRow([`${dimLabel} — ${measureLabel(pivotMeasure)}`]);
      const headerCols = [dimLabel, ...revList.map((r) => revName(r)), ...(revList.length >= 2 ? ['Δ (first→last)'] : []), 'Total'];
      styleHeader(s3.addRow(headerCols));
      pivotRows.forEach((row) => s3.addRow([row.key, ...revList.map((r) => row.byRev[r.rev] || 0), ...(revList.length >= 2 ? [row.delta] : []), row.total]));
      s3.columns.forEach((c) => { c.width = 18; });
    }

    // ISO churn (only re-issued ISOs)
    const churnRows = (churn?.isos || []).filter((i) => i.reissues > 0);
    if (churnRows.length) {
      const s4 = wb.addWorksheet('ISO churn');
      styleHeader(s4.addRow(['ISO Sheet', 'Batches', 'Re-issues', 'First Version', 'Latest Version', 'First Qty', 'Latest Qty', 'First Hours', 'Latest Hours', 'Δ Hours', 'Σ Hours (all revs)']));
      churnRows.forEach((i) => s4.addRow([i.iso_sheet_number, i.batches, i.reissues, i.firstVersion, i.latestVersion, i.firstQty, i.latestQty, i.firstHours, i.latestHours, i.dHours, i.totalHours]));
      s4.columns.forEach((c) => { c.width = 15; });
    }
    return wb;
  }, [data, revList, isoDiff, pivotDim, pivotRows, pivotMeasure, churn]);

  const buildPdfDoc = useCallback(async () => {
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber: estimateNumber, omitJobLine: true, margin: 14 });
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(`MTO Revision Comparison — ${estimateNumber}`, 14, letterheadY);
    doc.setTextColor(90, 90, 90); doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()}`, 14, letterheadY + 6);
    const headStyles = { fillColor: [99, 102, 241], textColor: 255 };
    const baseOpts = { headStyles, styles: { fontSize: 8, cellPadding: 1.5 }, margin: { left: 14, right: 14 } };

    autoTable(doc, {
      ...baseOpts,
      startY: letterheadY + 10,
      head: [['Metric', ...revList.map((r) => revName(r))]],
      body: [
        ...METRICS.map((m) => [m.label, ...revList.map((r) => { const v = data.totals?.[r.rev]?.[m.key] || 0; return m.money ? money(v) : m.fmt(v); })]),
        ['ISO sheets', ...revList.map((r) => String(data.totals?.[r.rev]?.isoCount || 0))],
      ],
    });
    let y = doc.lastAutoTable.finalY + 8;

    if (isoDiff.length) {
      doc.setFontSize(11); doc.setTextColor(51, 65, 85);
      doc.text(`ISO diff: ${revName(allRevs.find((r) => r.rev === baseRev))} → ${revName(allRevs.find((r) => r.rev === targetRev))}`, 14, y);
      autoTable(doc, {
        ...baseOpts,
        startY: y + 3,
        head: [['ISO Sheet', 'Status', 'Version', 'Δ Qty', 'Δ Hours', 'Δ Cost (CAD)']],
        body: isoDiff.map((r) => [r.iso, r.status, `${r.verB ?? '-'}→${r.verT ?? '-'}`, `${r.dQty > 0 ? '+' : ''}${Number(r.dQty).toLocaleString(undefined, { maximumFractionDigits: 1 })}`, `${r.dHours > 0 ? '+' : ''}${Number(r.dHours).toFixed(1)}`, `${r.dCad > 0 ? '+' : ''}${money(r.dCad)}`]),
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (pivotDim && pivotRows.length) {
      const dimLabel = (PIVOT_DIMS.find((d) => d.key === pivotDim) || {}).label || pivotDim;
      const m = METRICS.find((x) => x.key === pivotMeasure) || {};
      const fmt = (v) => (m.money ? money(v) : (m.fmt ? m.fmt(v) : Number(v).toLocaleString()));
      if (y > 170) { doc.addPage(); y = 20; }
      doc.setFontSize(11); doc.setTextColor(51, 65, 85);
      doc.text(`Pivot: ${dimLabel} (${measureLabel(pivotMeasure)})`, 14, y);
      autoTable(doc, {
        ...baseOpts,
        startY: y + 3,
        head: [[dimLabel, ...revList.map((r) => revName(r)), ...(revList.length >= 2 ? ['Δ'] : []), 'Total']],
        body: pivotRows.map((row) => [row.key, ...revList.map((r) => fmt(row.byRev[r.rev] || 0)), ...(revList.length >= 2 ? [`${row.delta > 0 ? '+' : ''}${fmt(row.delta)}`] : []), fmt(row.total)]),
      });
    }
    return doc;
  }, [data, revList, isoDiff, pivotDim, pivotRows, pivotMeasure, allRevs, baseRev, targetRev, estimateNumber]);

  const handleDownloadExcel = async () => {
    try {
      setExporting('excel');
      const wb = await buildWorkbook();
      const buf = await wb.xlsx.writeBuffer();
      downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), exportFilename({ job: estimateNumber, reportSlug: 'MTO_Compare', ext: 'xlsx' }));
    } catch (e) { window.alert('Excel export failed: ' + (e?.message || e)); }
    finally { setExporting(null); }
  };

  const handleDownloadPdf = async () => {
    try {
      setExporting('pdf');
      const doc = await buildPdfDoc();
      doc.save(exportFilename({ job: estimateNumber, reportSlug: 'MTO_Compare', ext: 'pdf' }));
    } catch (e) { window.alert('PDF export failed: ' + (e?.message || e)); }
    finally { setExporting(null); }
  };

  const handleSendEmail = async () => {
    const recipients = emailAddresses.split(/[,;\s]+/).filter((e) => e.includes('@'));
    if (recipients.length === 0) { setEmailResult({ type: 'error', message: 'Enter at least one valid email address.' }); return; }
    setEmailSending(true); setEmailResult(null);
    try {
      const doc = await buildPdfDoc();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const filename = exportFilename({ job: estimateNumber, reportSlug: 'MTO_Compare', ext: 'pdf' });
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: `MTO Revision Comparison — ${estimateNumber}`,
        htmlBody: `<p>Please find attached the MTO revision comparison report for estimate <strong>${estimateNumber}</strong>.</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: pdfBase64 }],
        feature: 'analytics_report',
      });
      if (resp.data?.success) { setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s).` }); setEmailAddresses(''); }
      else setEmailResult({ type: 'error', message: resp.data?.message || 'Failed to send email.' });
    } catch (e) { setEmailResult({ type: 'error', message: e?.response?.data?.message || e?.message || 'Failed to send email.' }); }
    finally { setEmailSending(false); }
  };

  if (!isOpen) return null;

  const th = { padding: '6px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'left', fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap' };
  const td = { padding: '5px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '0.74rem' };

  return (
    <>
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
      <div style={{ backgroundColor: 'white', borderRadius: '10px', width: '94%', maxWidth: '1100px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 55%)', zIndex: 2 }}>
          <h3 style={{ margin: 0, color: '#0f172a' }}>📈 Compare MTO Revisions — {estimateNumber}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {data && !loading && (
              <>
                <button onClick={() => { setEmailResult(null); setShowEmailModal(true); }} title="Email report as PDF"
                  style={{ padding: '5px 10px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>📧 Email</button>
                <button onClick={handleDownloadPdf} disabled={exporting === 'pdf'} title="Download report as PDF"
                  style={{ padding: '5px 10px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: exporting === 'pdf' ? 'wait' : 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>{exporting === 'pdf' ? '…' : '📥 PDF'}</button>
                <button onClick={handleDownloadExcel} disabled={exporting === 'excel'} title="Download report as Excel"
                  style={{ padding: '5px 10px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: exporting === 'excel' ? 'wait' : 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>{exporting === 'excel' ? '…' : '📊 Excel'}</button>
              </>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
          </div>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          {loading && <p style={{ color: '#64748b' }}>Loading comparison…</p>}
          {error && <p style={{ color: '#dc2626' }}>{error}</p>}

          {data && !loading && (
            <>
              {/* Revision selector (Plan 12 multi-rev) */}
              {allRevs.length > 1 && (
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ margin: '0 0 0.4rem', color: '#334155' }}>Revisions to compare</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {allRevs.map((r) => {
                      const on = !selectedRevs || selectedRevs.includes(r.rev);
                      return (
                        <button key={r.rev} onClick={() => toggleRev(r.rev)} style={{
                          padding: '4px 10px', borderRadius: '999px', fontSize: '0.74rem', cursor: 'pointer',
                          border: `1px solid ${on ? '#6366f1' : '#cbd5e1'}`,
                          backgroundColor: on ? '#eef2ff' : 'white', color: on ? '#4338ca' : '#64748b', fontWeight: on ? 600 : 400,
                        }} title={r.client_rev ? `client ${r.client_rev}` : ''}>
                          {on ? '✓ ' : ''}{r.is_consolidated ? '🧩 Consolidated ISOs' : `Rev ${r.rev}${r.client_rev ? ` (${r.client_rev})` : ''}`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* KPI cards: bid (first selected rev) → latest selected rev at a glance */}
              {revList.length >= 2 && (() => {
                const first = data.totals?.[revList[0].rev] || {};
                const last = data.totals?.[revList[revList.length - 1].rev] || {};
                const numFmt = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
                return (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 6 }}>
                      {revName(revList[0])} → {revName(revList[revList.length - 1])}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                      <KpiCard label="Quantity" icon="📦" first={first.qty || 0} last={last.qty || 0} fmt={numFmt} />
                      <KpiCard label="Labour Hours" icon="⏱" first={first.hours || 0} last={last.hours || 0} fmt={numFmt} />
                      <KpiCard label="Grand Total" icon="💰" first={(first.grand_cad || 0)} last={(last.grand_cad || 0)} fmt={money} />
                      <KpiCard label="ISO Sheets" icon="📐" first={first.isoCount || 0} last={last.isoCount || 0} fmt={(v) => String(Math.round(v))} />
                    </div>
                  </div>
                );
              })()}

              {/* Per-revision totals table */}
              <h4 style={{ margin: '0 0 0.5rem', color: '#334155' }}>Per-revision totals</h4>
              <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px' }}>
                  <thead>
                    <tr>
                      <th style={th}>Metric</th>
                      {revList.map((r, i) => (
                        <th key={r.rev} style={{ ...th, borderTop: `3px solid ${REV_PALETTE[i % REV_PALETTE.length]}` }}>
                          {r.is_consolidated ? '🧩 Consolidated ISOs' : `Rev ${r.rev}`}
                          {r.is_consolidated
                            ? <div style={{ fontWeight: 400, color: '#94a3b8' }}>{r.batch_count || 0} batch(es), latest per ISO</div>
                            : <>
                                {r.client_rev ? <div style={{ fontWeight: 400, color: '#94a3b8' }}>client {r.client_rev}</div> : null}
                                {r.transmittal_number ? <div style={{ fontWeight: 400, color: '#94a3b8' }}>TX {r.transmittal_number}</div> : null}
                                {r.created_at ? <div style={{ fontWeight: 400, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</div> : null}
                              </>}
                        </th>
                      ))}
                      {revList.length >= 2 && <th style={{ ...th, textAlign: 'right' }}>Δ (first→last)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {METRICS.map((m) => {
                      const dv = revList.length >= 2
                        ? (data.totals?.[revList[revList.length - 1].rev]?.[m.key] || 0) - (data.totals?.[revList[0].rev]?.[m.key] || 0)
                        : 0;
                      return (
                      <tr key={m.key}>
                        <td style={{ ...td, fontWeight: 600, color: '#334155' }}>{m.label}</td>
                        {revList.map((r) => {
                          const v = data.totals?.[r.rev]?.[m.key] || 0;
                          return <td key={r.rev} style={td}>{m.money ? money(v) : m.fmt(v)}</td>;
                        })}
                        {revList.length >= 2 && (
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: dv > 0 ? '#16a34a' : (dv < 0 ? '#dc2626' : '#64748b') }}>
                            {dv > 0 ? '+' : ''}{m.money ? money(dv) : m.fmt(dv)}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                    <tr>
                      <td style={{ ...td, fontWeight: 600, color: '#334155' }}>ISO sheets</td>
                      {revList.map((r) => <td key={r.rev} style={td}>{data.totals?.[r.rev]?.isoCount || 0}</td>)}
                      {revList.length >= 2 && (() => {
                        const dv = (data.totals?.[revList[revList.length - 1].rev]?.isoCount || 0) - (data.totals?.[revList[0].rev]?.isoCount || 0);
                        return <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: dv > 0 ? '#16a34a' : (dv < 0 ? '#dc2626' : '#64748b') }}>{dv > 0 ? '+' : ''}{dv}</td>;
                      })()}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* ISO churn (ISO-batch stream evolution) */}
              {churn && churn.summary && churn.summary.totalBatches > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 0.5rem', color: '#334155' }}>🧩 ISO churn — across {churn.summary.totalBatches} batch(es)</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem' }}>
                    {[
                      { label: 'ISO sheets', value: churn.summary.totalIsos },
                      { label: 'Re-issued (>1 batch)', value: churn.summary.reissuedIsos },
                      { label: 'Modified', value: churn.summary.modifiedIsos },
                      { label: 'Re-issued 2+ times', value: churn.summary.multiplyReissuedIsos },
                      { label: 'Net hours from re-issues', value: Number(churn.summary.netHoursFromReissues || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) },
                    ].map((c) => (
                      <div key={c.label} style={{ flex: '1 1 130px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.6rem 0.8rem' }}>
                        <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{c.value}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                  {(churn.isos || []).some((i) => i.reissues > 0) && (
                    <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto', border: '1px solid #f1f5f9', borderRadius: '6px' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '720px' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                          <tr>
                            <th style={th}>ISO Sheet</th>
                            <th style={th}>Batches</th>
                            <th style={th}>Re-issues</th>
                            <th style={th}>Version</th>
                            <th style={th}>Qty (first→latest)</th>
                            <th style={th}>Hours (first→latest)</th>
                            <th style={th}>Δ Hours</th>
                            <th style={th} title="Sum of this ISO across every batch it appeared in">Σ Hours (all revs)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(churn.isos || []).filter((i) => i.reissues > 0).map((i) => (
                            <tr key={i.iso_sheet_number} style={{ backgroundColor: i.modified ? '#fffbeb' : 'white' }}>
                              <td style={td}>{i.iso_sheet_number}</td>
                              <td style={td}>{i.batches}</td>
                              <td style={td}>{i.reissues}</td>
                              <td style={td}>{i.firstVersion ?? '-'} → {i.latestVersion ?? '-'}</td>
                              <td style={td}>{Number(i.firstQty).toLocaleString()} → {Number(i.latestQty).toLocaleString()}</td>
                              <td style={td}>{Number(i.firstHours).toFixed(1)} → {Number(i.latestHours).toFixed(1)}</td>
                              <td style={{ ...td, color: i.dHours > 0 ? '#16a34a' : (i.dHours < 0 ? '#dc2626' : '#64748b') }}>{i.dHours > 0 ? '+' : ''}{Number(i.dHours).toFixed(1)}</td>
                              <td style={td}>{Number(i.totalHours).toFixed(1)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {churnLoading && <p style={{ color: '#64748b' }}>Loading ISO churn…</p>}

              {/* Trend chart */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                <h4 style={{ margin: 0, color: '#334155' }}>Trend</h4>
                <select value={metric} onChange={(e) => setMetric(e.target.value)} style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
                  {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
              <div style={{ height: 260, marginBottom: '1.5rem', background: '#fafbff', border: '1px solid #eef2f7', borderRadius: 10, padding: '0.5rem 0.25rem' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 22, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7ebf3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => (METRICS.find((m) => m.key === metric)?.money ? money(v) : Number(v).toLocaleString())} />
                    <Legend />
                    <Bar dataKey="value" name={METRICS.find((m) => m.key === metric)?.label} radius={[6, 6, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={REV_PALETTE[i % REV_PALETTE.length]} />)}
                      <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: '#475569', fontWeight: 600 }}
                        formatter={(v) => (METRICS.find((m) => m.key === metric)?.money ? money(v) : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }))} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Base vs target diff */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, color: '#334155' }}>ISO diff</h4>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Base</span>
                <select value={baseRev ?? ''} onChange={(e) => setBaseRev(Number(e.target.value))} style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
                  {revList.map((r) => <option key={r.rev} value={r.rev}>{revName(r)}</option>)}
                </select>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>→ Target</span>
                <select value={targetRev ?? ''} onChange={(e) => setTargetRev(Number(e.target.value))} style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
                  {revList.map((r) => <option key={r.rev} value={r.rev}>{revName(r)}</option>)}
                </select>
                <button onClick={fetchInsight} disabled={insightLoading} style={{ padding: '4px 12px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '4px', cursor: insightLoading ? 'not-allowed' : 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>
                  {insightLoading ? 'Thinking…' : ((selectedRevs || []).length >= 3 ? '✨ AI insight (series)' : '✨ AI insight')}
                </button>
              </div>

              {insight && (
                <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.9rem 1rem', marginBottom: '1rem', color: '#0c4a6e', fontSize: '0.84rem', lineHeight: 1.5 }}>
                  {insight.insight}
                  <div style={{ marginTop: '6px', fontSize: '0.7rem', color: '#0369a1' }}>{insight.usedAi ? '✨ Generated by AI' : 'ℹ️ Template summary (AI unavailable)'}</div>
                </div>
              )}

              {isoDiff.length > 0 && (() => {
                const counts = isoDiff.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
                const netHours = isoDiff.reduce((s, r) => s + r.dHours, 0);
                const netCad = isoDiff.reduce((s, r) => s + r.dCad, 0);
                const chip = (bg, fg, text) => <span style={{ background: bg, color: fg, borderRadius: 999, padding: '3px 12px', fontSize: '0.74rem', fontWeight: 700 }}>{text}</span>;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '0.6rem' }}>
                    {counts.added ? chip('#dcfce7', '#15803d', `＋ ${counts.added} added`) : null}
                    {counts.removed ? chip('#fee2e2', '#b91c1c', `－ ${counts.removed} removed`) : null}
                    {counts.changed ? chip('#fef3c7', '#b45309', `~ ${counts.changed} changed`) : null}
                    {chip('#eef2ff', '#4338ca', `Net ${netHours > 0 ? '+' : ''}${netHours.toFixed(1)} hrs`)}
                    {chip('#f1f5f9', '#334155', `Net ${netCad > 0 ? '+' : ''}${money(netCad)}`)}
                  </div>
                );
              })()}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '760px' }}>
                  <thead>
                    <tr>
                      <th style={th}>ISO Sheet</th>
                      <th style={th}>Status</th>
                      <th style={th}>Version</th>
                      <th style={th}>Qty (base→target)</th>
                      <th style={th}>Δ Qty</th>
                      <th style={th}>Hours (base→target)</th>
                      <th style={th}>Δ Hours</th>
                      <th style={th}>Δ Cost (CAD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isoDiff.length === 0 && (
                      <tr><td style={td} colSpan={8}>No differences between {revName(allRevs.find((r) => r.rev === baseRev))} and {revName(allRevs.find((r) => r.rev === targetRev))}.</td></tr>
                    )}
                    {isoDiff.map((r) => (
                      <tr key={r.iso} style={{ backgroundColor: r.status === 'added' ? '#f0fdf4' : (r.status === 'removed' ? '#fef2f2' : 'transparent') }}>
                        <td style={{ ...td, fontWeight: 600 }}>{r.iso}</td>
                        <td style={td}><StatusPill status={r.status} /></td>
                        <td style={td}>{r.verB ?? '-'} → {r.verT ?? '-'}</td>
                        <td style={td}>{Number(r.baseQty).toLocaleString()} → {Number(r.targetQty).toLocaleString()}</td>
                        <td style={{ ...td, color: r.dQty > 0 ? '#16a34a' : (r.dQty < 0 ? '#dc2626' : '#64748b') }}>{r.dQty > 0 ? '+' : ''}{Number(r.dQty).toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                        <td style={td}>{Number(r.baseHours).toFixed(1)} → {Number(r.targetHours).toFixed(1)}</td>
                        <td style={{ ...td, color: r.dHours > 0 ? '#16a34a' : (r.dHours < 0 ? '#dc2626' : '#64748b') }}>{r.dHours > 0 ? '+' : ''}{Number(r.dHours).toFixed(1)}</td>
                        <td style={{ ...td, color: r.dCad > 0 ? '#16a34a' : (r.dCad < 0 ? '#dc2626' : '#64748b') }}>{r.dCad > 0 ? '+' : ''}{money(r.dCad)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Configurable pivot (Plan 12): dimension × revisions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '1.5rem 0 0.6rem', flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, color: '#334155' }}>Pivot</h4>
                <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Group by</span>
                <select value={pivotDim} onChange={(e) => setPivotDim(e.target.value)} style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
                  <option value="">(choose a dimension)</option>
                  {PIVOT_DIMS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
                {pivotDim && (
                  <>
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Measure</span>
                    <select value={pivotMeasure} onChange={(e) => setPivotMeasure(e.target.value)} style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
                      {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                    <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{pivotRows.length} rows × {revList.length} revs</span>
                  </>
                )}
              </div>
              {pivotDim && (
                <div style={{ overflowX: 'auto' }}>
                  {pivotLoading ? <p style={{ color: '#64748b' }}>Loading pivot…</p> : (
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '700px' }}>
                      <thead>
                        <tr>
                          <th style={th}>{(PIVOT_DIMS.find((d) => d.key === pivotDim) || {}).label || pivotDim}</th>
                          {revList.map((r) => <th key={r.rev} style={{ ...th, textAlign: 'right' }}>{r.is_consolidated ? 'Consol. ISOs' : `Rev ${r.rev}`}</th>)}
                          {revList.length >= 2 && (
                            <th style={{ ...th, textAlign: 'right' }} title={`Change from ${revName(revList[0])} to ${revName(revList[revList.length - 1])}`}>
                              Δ ({revName(revList[0])}→{revName(revList[revList.length - 1])})
                            </th>
                          )}
                          <th style={{ ...th, textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pivotRows.length === 0 && <tr><td style={td} colSpan={revList.length + 2 + (revList.length >= 2 ? 1 : 0)}>No data.</td></tr>}
                        {pivotRows.map((row) => {
                          const m = METRICS.find((x) => x.key === pivotMeasure) || {};
                          const fmt = (v) => (m.money ? money(v) : (m.fmt ? m.fmt(v) : Number(v).toLocaleString()));
                          return (
                            <tr key={row.key}>
                              <td style={{ ...td, fontWeight: 500 }} title={row.key}>{row.key}</td>
                              {revList.map((r) => <td key={r.rev} style={{ ...td, textAlign: 'right' }}>{fmt(row.byRev[r.rev] || 0)}</td>)}
                              {revList.length >= 2 && (
                                <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: row.delta > 0 ? '#16a34a' : (row.delta < 0 ? '#dc2626' : '#64748b') }}>
                                  {row.delta > 0 ? '+' : ''}{fmt(row.delta)}
                                </td>
                              )}
                              <td style={{ ...td, textAlign: 'right', fontWeight: 700, backgroundColor: '#f8fafc' }}>{fmt(row.total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td style={{ ...td, fontWeight: 700, backgroundColor: '#f1f5f9' }}>Total</td>
                          {revList.map((r) => {
                            const m = METRICS.find((x) => x.key === pivotMeasure) || {};
                            const fmt = (v) => (m.money ? money(v) : (m.fmt ? m.fmt(v) : Number(v).toLocaleString()));
                            const colTotal = pivotRows.reduce((s, row) => s + (row.byRev[r.rev] || 0), 0);
                            return <td key={r.rev} style={{ ...td, textAlign: 'right', fontWeight: 700, backgroundColor: '#f1f5f9' }}>{fmt(colTotal)}</td>;
                          })}
                          {revList.length >= 2 && (() => {
                            const m = METRICS.find((x) => x.key === pivotMeasure) || {};
                            const fmt = (v) => (m.money ? money(v) : (m.fmt ? m.fmt(v) : Number(v).toLocaleString()));
                            const deltaTotal = pivotRows.reduce((s, row) => s + row.delta, 0);
                            return <td style={{ ...td, textAlign: 'right', fontWeight: 700, backgroundColor: '#f1f5f9', color: deltaTotal > 0 ? '#16a34a' : (deltaTotal < 0 ? '#dc2626' : '#334155') }}>{deltaTotal > 0 ? '+' : ''}{fmt(deltaTotal)}</td>;
                          })()}
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, backgroundColor: '#e2e8f0' }}>
                            {(() => { const m = METRICS.find((x) => x.key === pivotMeasure) || {}; const fmt = (v) => (m.money ? money(v) : (m.fmt ? m.fmt(v) : Number(v).toLocaleString())); return fmt(pivotRows.reduce((s, row) => s + row.total, 0)); })()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

      {/* Email report modal */}
      {showEmailModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}
          onClick={() => { if (!emailSending) setShowEmailModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '10px', width: '90%', maxWidth: '460px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>📧 Email Comparison Report</h3>
              <button onClick={() => { if (!emailSending) setShowEmailModal(false); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: '#475569' }}>
                Enter email addresses separated by commas. A PDF report (totals, ISO diff{pivotDim ? ', pivot' : ''}) will be attached.
              </p>
              <textarea
                value={emailAddresses}
                onChange={(e) => setEmailAddresses(e.target.value)}
                placeholder="name@laframboise.net, other@laframboise.net"
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', resize: 'vertical' }}
              />
              {emailResult && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: emailResult.type === 'error' ? '#dc2626' : '#16a34a' }}>
                  {emailResult.type === 'error' ? '⚠️ ' : '✓ '}{emailResult.message}
                </div>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button onClick={() => setShowEmailModal(false)} disabled={emailSending}
                  style={{ padding: '7px 14px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Close</button>
                <button onClick={handleSendEmail} disabled={emailSending || !emailAddresses.trim()}
                  style={{ padding: '7px 14px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px', cursor: emailSending || !emailAddresses.trim() ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600, opacity: emailSending || !emailAddresses.trim() ? 0.6 : 1 }}>
                  {emailSending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
